import '@core/icons/solid';
import { IconName } from '@fortawesome/pro-regular-svg-icons';
import { routesPaths } from '@routes/helpers/constants';
import { PermissionsService } from '@services/permissions';
import { accountStore } from '@stores/bootstrap-stores/account';
import { hasItems } from '@utils/vanillaJs';
import { Module, permissions, UserType } from '@vincerosoft/assessfluence-types';

const getUserTypes = (module: Module): UserType[] => {
  const validPermissions = permissions.filter((perm) => perm.module === module);

  const userTypes = hasItems(validPermissions)
    ? validPermissions.flatMap((perm) => perm.user_types)
    : [];

  return [...new Set(userTypes)];
};

export type FlyoutLeafItem = { label: string; to: string };
export type FlyoutSectionItem = { label: string; items: FlyoutLeafItem[] };
export type FlyoutItem = FlyoutLeafItem | FlyoutSectionItem;

export const isFlyoutSectionItem = (item: FlyoutItem): item is FlyoutSectionItem => 'items' in item;

// todo - consider if 'home' and 'user-profile' should be added as modules in the backend for consistency, instead of handling them separately in the frontend
// translation keys for module labels and descriptions should be in the format 'nav_list.{module_id}' and 'nav_list.{module_id}_description' respectively to be picked up by the translation service
export const getModuleRegistry = (): {
  [key in Module]: {
    id: Module | 'home' | 'user-profile';
    label: string;
    description: string;
    icon: IconName;
    userTypes: UserType[];
    required?: boolean;
    children?: FlyoutItem[];
    routerLink: string;
  };
} => {
  const scope = accountStore.getState().scope;
  return {
    help: {
      id: 'help',
      label: 'Help guide',
      description: 'Access to help documentation',
      icon: 'question',
      userTypes: getUserTypes('help'),
      routerLink: '/help'
    },
    insights: {
      id: 'insights',
      label: 'Insights',
      description: 'Quickly analyze data and generate reports',
      icon: 'flag',
      userTypes: getUserTypes('insights'),
      routerLink: '/insights'
    },
    entities: {
      id: 'entities',
      label: 'Accounts',
      description: 'Read/Write accounts profile, settings and access grants',
      icon: 'building-user',
      userTypes: getUserTypes('entities'),
      routerLink: routesPaths.accounts.root,
      children: [
        { label: 'Entities', to: routesPaths.accounts.entities.root },
        ...(PermissionsService.canRead('group-entity')
          ? [{ label: 'Group Entities', to: routesPaths.accounts.groupEntities.root }]
          : []),
        { label: 'Data Pools', to: routesPaths.accounts.dataPools.root },
        { label: 'Tags', to: routesPaths.accounts.tags }
      ]
    },
    responses: {
      id: 'responses',
      label: 'Responses',
      description: 'View and edit responses',
      icon: 'list-check',
      userTypes: getUserTypes('responses'),
      routerLink: routesPaths.responses.root
    },
    approvals: {
      id: 'approvals',
      label: 'Approvals',
      description: 'View and edit approvals',
      icon: 'circle-exclamation-check',
      userTypes: getUserTypes('approvals'),
      routerLink: routesPaths.approvals.root
    },
    studio: {
      id: 'studio',
      label: 'Studio',
      description: 'Create and edit forms, reports and automations',
      icon: 'clipboard-check',
      userTypes: getUserTypes('studio'),
      routerLink: routesPaths.studio.root,
      children: [
        { label: 'Forms', to: routesPaths.studio.forms.root },
        ...(PermissionsService.canRead('form-group')
          ? [{ label: 'Form Groups', to: routesPaths.studio.formGroups.root }]
          : []),
        ...(PermissionsService.canRead('report')
          ? [{ label: 'Reporting', to: routesPaths.studio.reporting.root }]
          : []),
        ...(PermissionsService.canRead('expression')
          ? [{ label: 'Expressions', to: routesPaths.studio.expressions.root }]
          : []),
        ...(PermissionsService.canRead('workflow')
          ? [{ label: 'Automations', to: routesPaths.studio.automations.root }]
          : []),
        ...(PermissionsService.canModify('form')
          ? [{ label: 'Taxonomies', to: routesPaths.studio.taxonomies.root }]
          : [])
      ]
    },
    campaigns: {
      id: 'campaigns',
      label: 'Campaigns',
      description: 'Launch campaigns and track responses',
      icon: 'megaphone',
      userTypes: getUserTypes('campaigns'),
      routerLink: routesPaths.campaigns.root,
      children: [
        { label: 'Campaigns', to: routesPaths.campaigns.root },
        { label: 'Tags', to: routesPaths.campaigns.tags }
      ]
    },
    invitations: {
      id: 'invitations',
      label: 'Invitations',
      description: 'View invitations and related responses',
      icon: 'paper-plane',
      userTypes: getUserTypes('invitations'),
      routerLink: routesPaths.invitations.root
    },
    workspace: {
      id: 'workspace',
      label: 'Workspace',
      description: 'Self-service forms that can be retaken as needed',
      icon: 'cubes-stacked',
      userTypes: getUserTypes('workspace'),
      routerLink: routesPaths.workspace.root
    },
    'learning-center': {
      id: 'learning-center',
      label: 'Learning Center',
      description: 'Start a Learning Center program and track progress',
      icon: 'lightbulb',
      userTypes: getUserTypes('learning-center'),
      routerLink: routesPaths.learningCenter.root
    },
    users: {
      id: 'users',
      label: 'Identity & Access',
      description: 'Read/Write users, roles and organizational nodes',
      icon: 'users',
      userTypes: getUserTypes('users'),
      children: [
        ...(PermissionsService.canReadOwn('user')
          ? [{ label: 'Users', to: routesPaths.iam.users.root }]
          : []),
        ...(PermissionsService.canReadOwn('list')
          ? [{ label: 'Lists', to: routesPaths.iam.lists.root }]
          : []),
        ...(PermissionsService.canRead('role')
          ? [{ label: 'Roles', to: routesPaths.iam.roles.root }]
          : []),
        ...(PermissionsService.canRead('user-group')
          ? [{ label: 'User Groups', to: routesPaths.iam.userGroups.root }]
          : []),
        ...(PermissionsService.canRead('node')
          ? [{ label: 'Nodes', to: routesPaths.iam.nodes.root }]
          : [])
      ],
      routerLink: routesPaths.iam.root
    },
    datapools: {
      id: 'datapools',
      label: 'Datapools',
      description: 'Cross-entity response visibility for collaborative insights',
      icon: 'merge',
      userTypes: getUserTypes('datapools'),
      routerLink: routesPaths.datapools.root
    },
    filestore: {
      id: 'filestore',
      label: 'Filestore',
      description: 'Read/Write media files',
      icon: 'files',
      userTypes: getUserTypes('filestore'),
      routerLink: routesPaths.files.root
    },
    solvers: {
      id: 'solvers',
      label: 'Solvers',
      description: 'Create and run solvers to generate optimal resource assignments',
      icon: 'arrow-progress',
      userTypes: getUserTypes('solvers'),
      routerLink: routesPaths.solvers.root
    },
    tables: {
      id: 'tables',
      label: 'Data tables',
      description: 'Store organisational data in a table for use in Studio',
      icon: 'database',
      userTypes: getUserTypes('tables'),
      routerLink: routesPaths.tables.root
    },
    account: {
      id: 'account',
      label: 'Account',
      description: 'Read/Write account settings and branding',
      icon: 'gear',
      userTypes: getUserTypes('account'),
      routerLink: routesPaths.accountSettings.root,
      children: [
        { label: 'Profile', to: routesPaths.accountSettings.profile },
        ...(scope === 'tenant'
          ? [
              { label: 'App Settings', to: routesPaths.accountSettings.settings },
              { label: 'Branding', to: routesPaths.accountSettings.branding }
            ]
          : []),
        ...(scope !== 'linked'
          ? [{ label: 'Emails', to: routesPaths.accountSettings.emails }]
          : []),
        { label: 'Logs', to: routesPaths.accountSettings.logs }
      ]
    },
    api: {
      id: 'api',
      label: 'API',
      description: 'Access to API documentation and API keys',
      icon: 'rectangle-api',
      userTypes: getUserTypes('api'),
      routerLink: routesPaths.api.root
    }
  };
};
